import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Logo } from "../components/Logo";
import { toast } from "sonner";

const API = window.__SG_API_BASE__;
const POLICY_LINKS = [
  { key: 'terms', label: 'Terms of Service', href: '/legal/terms' },
  { key: 'privacy', label: 'Privacy Policy', href: '/legal/privacy' },
  { key: 'community_guidelines', label: 'Community Guidelines', href: '/legal/community-guidelines' },
  { key: 'ai_disclaimer', label: 'AI Disclaimer', href: '/legal/ai-disclaimer' },
];

const CHOICES = [
  { id: "everyday_user", label: "Use StyleGenome for myself", icon: "👤" },
  { id: "stylist",       label: "Apply as a Stylist or Personal Shopper", icon: "👔" },
  { id: "designer",      label: "Apply as a Designer", icon: "🎨" },
  { id: "creator",       label: "Apply as a Creator or Influencer", icon: "📸" },
  { id: "merchant",      label: "Apply as a Boutique, Merchant, or Brand", icon: "🏢" },
  { id: "organization",  label: "Apply as an Organization, Event, or Team", icon: "🏛️" },
  { id: "ai_persona",    label: "Create an AI Style Persona", icon: "✨" },
  { id: "invited",       label: "I was invited", icon: "✉️" },
];

export const Landing = ({ initialMode = "landing" }) => {
  const navigate = useNavigate();
  const location = useLocation();
  // Founder Console separation — honor ?next=/founder/... after login.
  const nextParam = new URLSearchParams(location.search).get("next");
  const safeNext = nextParam && nextParam.startsWith("/") ? nextParam : null;

  // v3.1 — Emergent-managed Google Sign-In. Stash the internal destination,
  // then hand off to the managed auth page; AuthCallback validates `next`
  // against an internal whitelist (no open redirects).
  const startGoogleLogin = () => {
    try { window.sessionStorage?.setItem("sg.auth_next", safeNext || ""); } catch { /* storage blocked */ }
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const GoogleButton = ({ label = "Continue with Google" }) => (
    <>
      <button type="button" onClick={startGoogleLogin} data-testid="google-signin-btn"
        className="w-full flex items-center justify-center gap-3 border border-[#E5E4E2] bg-white text-[#0D0D0D] py-3.5 rounded-full font-sans text-xs uppercase tracking-widest hover:border-[#C7A96B] transition-colors">
        <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z"/>
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/>
          <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/>
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.3 9.14 5.38 12 5.38z"/>
        </svg>
        {label}
      </button>
      <div className="flex items-center gap-3 my-5">
        <div className="flex-1 h-px bg-[#E5E4E2]" />
        <span className="font-sans text-[9px] uppercase tracking-[0.2em] text-[#8E8A84]">or</span>
        <div className="flex-1 h-px bg-[#E5E4E2]" />
      </div>
    </>
  );
  const { user, login, signup, isAuthenticated } = useAuth();
  const [mode, setMode] = useState(initialMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [choice, setChoice] = useState("everyday_user");
  const [inviteCode, setInviteCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [legalVersions, setLegalVersions] = useState(null);
  // Exact-SHA P1 — post-signup verification panel state.
  const [verifyEmail, setVerifyEmail] = useState("");
  const [resendBusy, setResendBusy] = useState(false);
  const [resendDone, setResendDone] = useState(false);

  const maskEmail = (e) => {
    const [u, d] = (e || "").split("@");
    if (!d) return "your email";
    const dot = d.lastIndexOf(".");
    const dom = dot > 0 ? `${d[0]}•••${d.slice(dot)}` : `${d[0]}•••`;
    return `${u[0]}•••@${dom}`;
  };

  const resendVerification = async () => {
    setResendBusy(true);
    try {
      await axios.post(`${API}/api/auth/resend-verification`, { email: verifyEmail });
      setResendDone(true);
      toast.success("Verification email sent again.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not resend right now — try again in a minute.");
    } finally {
      setResendBusy(false);
    }
  };

  // Fetch current policy versions once so the signup payload records the
  // exact snapshot the user acknowledged.
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/api/legal/current`)
      .then(r => { if (!cancelled) setLegalVersions(r.data?.versions || null); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (isAuthenticated && user) {
      if (user.role !== "pending" && user.role !== "waitlist") {
        navigate(safeNext || "/home", { replace: true });
      }
    }
  }, [isAuthenticated, user, navigate, safeNext]);

  const onLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await login(email, password);
      if (res.status === "unverified") {
        setVerifyEmail(email);
        setResendDone(false);
        setMode("verify");
      } else {
        toast.success("Welcome back.");
        navigate(safeNext || "/home");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed.");
    } finally {
      setBusy(false);
    }
  };

  const onSignup = async (e) => {
    e.preventDefault();
    if (!termsAccepted) {
      toast.error("You must accept the required policies to create an account.");
      return;
    }
    setBusy(true);
    try {
      const isInvited = choice === "invited";
      if (isInvited) {
        // P0 — a private-beta invite is validated SERVER-SIDE before the
        // account exists. Missing/invalid/used/expired codes block signup
        // with an explicit state; the account-type selection alone never
        // grants access.
        const code = inviteCode.trim().toUpperCase();
        if (!code) {
          toast.error("Enter your invite code to sign up as an invited member.");
          return;
        }
        try {
          await axios.post(`${API}/api/beta-gate/redeem-code`, { code }, { withCredentials: true });
          toast.success("Invite code accepted.");
        } catch (err) {
          toast.error(err.response?.data?.detail || "That invite code is invalid, already used, or expired.");
          return;
        }
      }
      const actualChoice = isInvited ? "everyday_user" : choice;
      await signup(email, password, name, termsAccepted, actualChoice, isInvited, legalVersions);
      setVerifyEmail(email);
      setResendDone(false);
      setMode("verify");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Signup failed.");
    } finally {
      setBusy(false);
    }
  };

  if (mode === "landing") {
    return (
      <div className="min-h-screen bg-[#F7F5F1] flex flex-col items-center justify-center px-6">
        <div className="max-w-xl w-full text-center">
          <p className="font-sans text-[10px] uppercase tracking-[0.3em] text-[#C7A96B] mb-8 animate-fade-in">Personal Stylist (AI)</p>
          <div className="mb-10 animate-fade-in">
             <Logo variant="primary" height={100} className="mx-auto" />
          </div>
          <h1 className="font-serif text-4xl md:text-5xl tracking-tight text-[#0D0D0D] mb-6 leading-tight">Your style, refined by AI.</h1>
          <p className="font-sans font-light text-[#5C5852] text-lg mb-12 max-w-md mx-auto leading-relaxed">AI-assisted style, closet, and shopping guidance built around you.</p>
          <div className="flex flex-col gap-4 max-w-xs mx-auto">
            <button onClick={() => setMode("login")} className="bg-[#0D0D0D] text-white py-4 rounded-full font-sans text-xs uppercase tracking-widest hover:bg-black transition-colors shadow-lg">Sign In</button>
            <button onClick={() => setMode("choice")} className="border border-[#0D0D0D] text-[#0D0D0D] py-4 rounded-full font-sans text-xs uppercase tracking-widest hover:bg-white transition-colors">Join StyleGenome</button>
            <button onClick={() => navigate("/beta-gate")} className="text-[10px] uppercase tracking-[0.2em] text-[#8E8A84] hover:text-[#0D0D0D] mt-2">Have a beta code?</button>
          </div>
        </div>
      </div>
    );
  }

  if (mode === "verify") {
    return (
      <div className="min-h-screen bg-[#F7F5F1] flex flex-col items-center justify-center px-6 py-12">
        <div className="max-w-md w-full bg-white border border-[#E5E4E2] p-8 md:p-12 rounded-[40px] shadow-sm" data-testid="verify-email-panel">
          <Logo variant="mark" height={40} className="mb-6" />
          <h2 className="font-serif text-3xl mb-4">Check your inbox</h2>
          <p className="font-sans text-sm text-[#5C5852] leading-relaxed mb-2" data-testid="verify-email-masked">
            We sent a verification link to <span className="text-[#0D0D0D] font-medium">{maskEmail(verifyEmail)}</span>.
          </p>
          <p className="font-sans text-xs text-[#8E8A84] leading-relaxed mb-8">
            Click the link in that email to activate your account. It can take a minute to arrive — check your spam folder too.
          </p>
          <div className="space-y-4">
            <button onClick={resendVerification} disabled={resendBusy || resendDone}
              data-testid="verify-email-resend"
              className="w-full border border-[#0D0D0D] text-[#0D0D0D] py-3.5 rounded-full font-sans text-xs uppercase tracking-widest hover:bg-[#F7F5F1] transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
              {resendBusy ? "Sending…" : resendDone ? "Sent — check again" : "Resend verification email"}
            </button>
            <button onClick={() => setMode("login")} data-testid="verify-email-back-to-login"
              className="w-full bg-[#0D0D0D] text-white py-3.5 rounded-full font-sans text-xs uppercase tracking-widest hover:bg-black transition-colors">
              I've verified — sign in
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (mode === "choice") {
    return (
      <div className="min-h-screen bg-[#F7F5F1] flex flex-col items-center justify-center px-6 py-12">
        <div className="max-w-md w-full bg-white border border-[#E5E4E2] p-8 md:p-12 rounded-[40px] shadow-sm">
          <button onClick={() => setMode("landing")} className="mb-8 flex items-center gap-2 text-[#8E8A84] hover:text-[#0D0D0D] transition-colors font-sans text-[10px] uppercase tracking-widest">Back</button>
          <h2 className="font-serif text-3xl mb-8">How do you want to use StyleGenome?</h2>
          <div className="space-y-3 mb-10">
            {CHOICES.map(c => (
              <button key={c.id} onClick={() => { setChoice(c.id); setMode("signup"); }} className="w-full text-left p-4 border border-[#F7F5F1] rounded-2xl hover:border-[#C7A96B] transition-all flex items-center gap-4 group">
                <span className="text-2xl">{c.icon}</span>
                <span className="font-sans text-sm text-[#5C5852] group-hover:text-[#0D0D0D]">{c.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F5F1] flex flex-col items-center justify-center px-6 py-12">
      <div className="max-w-md w-full bg-white border border-[#E5E4E2] p-8 md:p-12 rounded-[40px] shadow-sm">
        <button onClick={() => setMode("choice")} className="mb-8 flex items-center gap-2 text-[#8E8A84] hover:text-[#0D0D0D] transition-colors font-sans text-[10px] uppercase tracking-widest">Back</button>
        <Logo variant="mark" height={40} className="mb-6" />
        <h2 className="font-serif text-3xl mb-8">{mode === "login" ? "Welcome Back" : "Sign Up"}</h2>
        <GoogleButton label={mode === "login" ? "Continue with Google" : "Sign up with Google"} />
        <form onSubmit={mode === "login" ? onLogin : onSignup} className="space-y-6">
          {mode === "signup" && (
            <div>
              <label htmlFor="signup-name" className="block font-sans text-[9px] uppercase tracking-widest text-[#8E8A84] mb-2">Name</label>
              <input type="text" id="signup-name" value={name} onChange={e => setName(e.target.value)} required className="w-full border-b border-[#E5E4E2] py-2 focus:outline-none focus:border-[#C7A96B] transition-colors font-sans text-sm" placeholder="Your Name" />
            </div>
          )}
          {mode === "signup" && choice === "invited" && (
            <div>
              <label htmlFor="signup-invite-code" className="block font-sans text-[9px] uppercase tracking-widest text-[#8E8A84] mb-2">Invite code</label>
              <input type="text" id="signup-invite-code" value={inviteCode} onChange={e => setInviteCode(e.target.value)} required
                data-testid="signup-invite-code"
                className="w-full border-b border-[#E5E4E2] py-2 focus:outline-none focus:border-[#C7A96B] transition-colors font-sans text-sm uppercase tracking-widest"
                placeholder="SG-XXXX-XXXX" />
              <p className="font-sans text-[10px] text-[#8E8A84] mt-1.5">The private beta requires a valid invite code — it's verified before your account is created.</p>
            </div>
          )}
          <div>
            <label htmlFor="auth-email" className="block font-sans text-[9px] uppercase tracking-widest text-[#8E8A84] mb-2">Email</label>
            <input type="email" id="auth-email" value={email} onChange={e => setEmail(e.target.value)} required className="w-full border-b border-[#E5E4E2] py-2 focus:outline-none focus:border-[#C7A96B] transition-colors font-sans text-sm" placeholder="email@example.com" />
          </div>
          <div>
            <label htmlFor="auth-password" className="block font-sans text-[9px] uppercase tracking-widest text-[#8E8A84] mb-2">Password</label>
            <input type="password" id="auth-password" value={password} onChange={e => setPassword(e.target.value)} required className="w-full border-b border-[#E5E4E2] py-2 focus:outline-none focus:border-[#C7A96B] transition-colors font-sans text-sm" />
            {mode === "login" && (
              <div className="text-right mt-2">
                <button type="button" onClick={() => navigate("/forgot-password")} data-testid="forgot-password-link"
                  className="font-sans text-[10px] uppercase tracking-[0.18em] text-[#8E8A84] hover:text-[#C7A96B] transition-colors">
                  Forgot password?
                </button>
              </div>
            )}
          </div>
          {mode === "signup" && (
            <div className="flex items-start gap-3 mt-4" data-testid="signup-legal-block">
              <input
                type="checkbox"
                id="terms"
                data-testid="signup-legal-checkbox"
                checked={termsAccepted}
                onChange={e => setTermsAccepted(e.target.checked)}
                className="mt-1"
              />
              <label htmlFor="terms" className="font-sans text-[12px] text-[#0D0D0D] leading-relaxed" data-testid="signup-legal-label">
                I agree to the{' '}
                {POLICY_LINKS.map((p, i) => (
                  <span key={p.key}>
                    <a
                      href={p.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline hover:text-[#C7A96B]"
                      data-testid={`signup-legal-link-${p.key}`}
                    >
                      {p.label}
                    </a>
                    {i < POLICY_LINKS.length - 2 ? ', ' : i === POLICY_LINKS.length - 2 ? ', and ' : '.'}
                  </span>
                ))}
              </label>
            </div>
          )}
          <button
            type="submit"
            disabled={busy || (mode === "signup" && !termsAccepted)}
            data-testid={mode === "signup" ? "signup-submit" : "login-submit"}
            className="w-full bg-[#0D0D0D] text-white py-4 rounded-full font-sans text-xs uppercase tracking-widest disabled:opacity-40 disabled:cursor-not-allowed shadow-md"
          >
            {busy ? "..." : (mode === "login" ? "Sign In" : "Create Account")}
          </button>
        </form>
      </div>
    </div>
  );
};
